import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Download, Loader2 } from "lucide-react";
import { PageHero } from "@/components/ui/PageHero";
import { buttonClass } from "@/components/ui/buttonStyles";
import { STORE_API_URL } from "@/config/constants";
import { useCart } from "@/hooks/useCart";
import { usePageSeo } from "@/hooks/usePageSeo";
import { useAppTranslation } from "@/i18n/useAppTranslation";
import { trackEvent } from "@/utils/analytics";

type OrderState =
  | { status: "loading" }
  | { status: "pending" }
  | { status: "paid"; email: string | null; items: { id: string; title: string; downloadUrl: string }[] }
  | { status: "failed" }
  | { status: "missing" };

const POLL_MS = 2500;
const MAX_POLLS = 12;

/** Thank-you page after Stripe Checkout (?session_id=…). Delivery itself is done by the webhook. */
export function PurchaseCompletePage() {
  usePageSeo("/recursos/compra");
  const { t } = useAppTranslation("resourcesPage");
  const { t: tNav } = useAppTranslation("nav");
  const [params] = useSearchParams();
  const sessionId = params.get("session_id") ?? "";
  const cart = useCart();
  const [order, setOrder] = useState<OrderState>({ status: "loading" });
  const tracked = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setOrder({ status: "missing" });
      return;
    }
    let cancelled = false;
    let polls = 0;
    let timer: number | undefined;

    async function load() {
      try {
        const res = await fetch(`${STORE_API_URL}/order?session_id=${encodeURIComponent(sessionId)}`);
        if (cancelled) return;
        if (!res.ok) {
          setOrder({ status: "missing" });
          return;
        }
        const body = (await res.json()) as {
          status: string;
          email: string | null;
          items: { id: string; title: string; downloadUrl: string }[];
        };
        if (cancelled) return;
        if (body.status === "paid") {
          setOrder({ status: "paid", email: body.email, items: body.items });
        } else if (body.status === "pending" && polls < MAX_POLLS) {
          polls += 1;
          setOrder({ status: "pending" });
          timer = window.setTimeout(load, POLL_MS);
        } else if (body.status === "pending") {
          setOrder({ status: "pending" });
        } else {
          setOrder({ status: "failed" });
        }
      } catch {
        if (!cancelled) setOrder({ status: "missing" });
      }
    }

    void load();
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [sessionId]);

  const { clear } = cart;
  useEffect(() => {
    if (order.status !== "paid" || tracked.current) return;
    tracked.current = true;
    clear();
    trackEvent("purchase_complete", { items: order.items.length });
  }, [order, clear]);

  const title =
    order.status === "paid"
      ? t("purchaseTitlePaid")
      : order.status === "failed"
        ? t("purchaseTitleFailed")
        : order.status === "missing"
          ? t("purchaseTitleMissing")
          : t("purchaseTitlePending");

  const lead =
    order.status === "paid"
      ? order.email
        ? t("purchaseLeadPaid", { email: order.email })
        : t("purchaseLeadPaidNoEmail")
      : order.status === "failed"
        ? t("purchaseLeadFailed")
        : order.status === "missing"
          ? t("purchaseLeadMissing")
          : t("purchaseLeadPending");

  return (
    <main id="main-content" tabIndex={-1} className="outline-none">
      <PageHero
        crumb={t("purchaseCrumb")}
        trail={[{ label: tNav("resources"), to: "/recursos" }]}
        title={title}
        lead={<p aria-live="polite">{lead}</p>}
      />
      <section className="bg-brand-bg">
        <div className="mx-auto max-w-3xl px-4 pb-24 sm:px-6 lg:px-8">
          {order.status === "loading" || order.status === "pending" ? (
            <div role="status" className="flex items-center gap-3 text-ink-muted">
              <Loader2 className="size-5 animate-spin text-teal-strong motion-reduce:animate-none" aria-hidden="true" />
              {t("purchaseTitlePending")}
            </div>
          ) : null}

          {order.status === "paid" ? (
            <div className="rounded-[1.75rem] border border-brand-primary/8 bg-white p-7 shadow-lit sm:p-9">
              <h2 className="font-display text-2xl font-semibold text-brand-primary">{t("purchaseDownloads")}</h2>
              <ul className="mt-6 divide-y divide-brand-primary/8">
                {order.items.map((item) => (
                  <li key={item.id} className="flex flex-wrap items-center justify-between gap-4 py-4">
                    <span className="flex items-center gap-3 font-semibold text-brand-primary">
                      <CheckCircle2 className="size-5 shrink-0 text-teal-strong" aria-hidden="true" />
                      {item.title}
                    </span>
                    <a
                      href={item.downloadUrl}
                      aria-label={t("purchaseDownloadAria", { title: item.title })}
                      className={buttonClass("primary", "md")}
                    >
                      <Download className="size-4" aria-hidden="true" />
                      {t("purchaseDownload")}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/recursos" className={buttonClass("outline", "md")}>
              {t("purchaseBack")}
            </Link>
            {order.status === "missing" ? (
              <Link to="/recursos/mis-compras" className={buttonClass("primary", "md")}>
                {t("myPurchases")}
              </Link>
            ) : null}
          </div>
        </div>
      </section>
    </main>
  );
}
